<template>
  <div class="pt-2">
    <div class="flex flex-col lg:flex-row gap-5 items-start w-full">
      <!-- Left Sidebar: Employee App Settings -->
      <div class="settings-sidebar-card p-4">
        <div class="px-2 pt-1 pb-3">
          <h2 class="text-xl font-bold text-gray-900 tracking-tight">Employee app settings</h2>
        </div>

        <!-- Search Box -->
        <div class="mb-3 px-1">
          <div class="settings-search-wrapper">
            <v-icon small class="search-icon">mdi-magnify</v-icon>
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search settings"
              class="settings-search-input"
            />
            <button
              v-if="searchQuery"
              @click="searchQuery = ''"
              type="button"
              class="clear-search-btn"
            >
              <v-icon x-small color="#94a3b8">mdi-close-circle</v-icon>
            </button>
          </div>
        </div>

        <!-- Categorized List -->
        <div class="settings-menu-list space-y-4 max-h-[calc(100vh-250px)] overflow-y-auto hide-scrollbar pr-1">
          <div v-for="category in filteredCategories" :key="category.name" class="category-block">
            <!-- Category Title -->
            <div class="category-title">
              {{ category.name }}
            </div>

            <!-- Items in Category -->
            <div class="space-y-1">
              <button
                v-for="item in category.items"
                :key="item.name"
                type="button"
                @click="handleTabSelect(item.originalIndex)"
                class="settings-menu-item"
                :class="{
                  'settings-item-active': activeTab === item.originalIndex
                }"
              >
                <!-- Icon Container -->
                <div
                  class="settings-icon-box"
                  :class="{
                    'settings-icon-active': activeTab === item.originalIndex,
                    'settings-icon-inactive': activeTab !== item.originalIndex
                  }"
                >
                  <v-icon
                    size="22"
                    :color="activeTab === item.originalIndex ? '#ffffff' : '#475569'"
                  >
                    {{ item.icon }}
                  </v-icon>
                </div>

                <!-- Title & Description -->
                <div class="min-w-0 flex-1">
                  <div
                    class="item-title truncate"
                    :class="{ 'item-title-active': activeTab === item.originalIndex }"
                  >
                    {{ item.title || item.label }}
                  </div>
                  <div class="item-description truncate">
                    {{ item.description }}
                  </div>
                </div>
              </button>
            </div>
          </div>

          <div v-if="filteredCategories.length === 0" class="text-center py-8 text-gray-400 text-sm">
            <v-icon color="#94a3b8" class="mb-2">mdi-text-box-search-outline</v-icon>
            <div>No settings found</div>
          </div>
        </div>
      </div>

      <!-- Right Content Area -->
      <div class="settings-content-card flex-1 min-w-0 p-6">
        <div v-for="(tab, index) in configTabs" :key="tab.name" v-show="activeTab === index">
          <div class="mb-5 flex justify-between items-center pb-3 border-b border-gray-100">
            <div>
              <h3 class="text-lg font-bold text-gray-900 mb-0.5">{{ tab.title || tab.label }}</h3>
              <p v-if="tab.description" class="text-xs text-gray-400 mb-0">{{ tab.description }}</p>
            </div>
          </div>

          <component
            :is="tab.component"
            :empAppSettings="empAppSettings"
            :appConfigOptions="appConfigOptions"
            @update-config="$emit('update-config', $event)"
            @save-filters="$emit('save-filters', $event)"
            @custom-activity-steps="$emit('custom-activity-steps', $event)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import GeneralSettings from './GeneralSettings.vue'
import DashboardCustomization from './DashboardCustomization.vue'
import StepperConfiguration from './StepperConfiguration.vue'
import OrgRepository from '../OrgConfigComponents/OrgUploadedDocs.vue'

export default {
  name: 'AppConfigTabsPanel',
  components: {
    GeneralSettings,
    DashboardCustomization,
    StepperConfiguration,
    OrgRepository
  },
  props: {
    empAppSettings: {
      type: Object,
      default: null
    },
    appConfigOptions: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      searchQuery: '',
      activeTab: 0,
      configTabs: [
        {
          name: 'generalSettings',
          label: 'General settings',
          title: 'General settings',
          description: 'Clock-in, data creation and daily timeline',
          icon: 'mdi-cellphone-cog',
          category: 'APPLICATION',
          component: GeneralSettings
        },
        {
          name: 'stepperConfiguration',
          label: 'Data creation flow',
          title: 'Data creation flow',
          description: 'Case and task creation flow steps',
          icon: 'mdi-transit-connection-variant',
          category: 'APPLICATION',
          component: StepperConfiguration
        },
        {
          name: 'dashboardCustomization',
          label: 'Dashboard customization',
          title: 'Dashboard customization',
          description: 'Employee mobile app dashboard cards',
          icon: 'mdi-view-dashboard-outline',
          category: 'WORKSPACE',
          component: DashboardCustomization
        },
        {
          name: 'orgRepository',
          label: 'Documents & Media',
          title: 'Documents & Media',
          description: 'Uploaded guidelines, media and policies',
          icon: 'mdi-folder-multiple-outline',
          category: 'WORKSPACE',
          component: OrgRepository
        }
      ]
    }
  },
  computed: {
    categories() {
      const categoryOrder = ['APPLICATION', 'WORKSPACE']
      const groups = {}
      categoryOrder.forEach((cat) => {
        groups[cat] = []
      })

      this.configTabs.forEach((tab, index) => {
        const cat = tab.category || 'APPLICATION'
        if (!groups[cat]) {
          groups[cat] = []
        }
        groups[cat].push({
          ...tab,
          originalIndex: index
        })
      })

      return Object.keys(groups)
        .filter((cat) => groups[cat].length > 0)
        .map((cat) => ({
          name: cat,
          items: groups[cat]
        }))
    },
    filteredCategories() {
      if (!this.searchQuery || !this.searchQuery.trim()) {
        return this.categories
      }
      const q = this.searchQuery.toLowerCase().trim()
      return this.categories
        .map((cat) => {
          const matchingItems = cat.items.filter((item) => {
            const title = (item.title || item.label || '').toLowerCase()
            const desc = (item.description || '').toLowerCase()
            return title.includes(q) || desc.includes(q)
          })
          return {
            name: cat.name,
            items: matchingItems
          }
        })
        .filter((cat) => cat.items.length > 0)
    }
  },
  methods: {
    handleTabSelect(index) {
      this.activeTab = index
    }
  }
}
</script>

<style scoped>
.settings-sidebar-card {
  width: 100%;
  background-color: #ffffff;
  border-radius: 20px;
  border: 1px solid #eef2f6;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
  box-sizing: border-box;
}

@media (min-width: 1024px) {
  .settings-sidebar-card {
    width: 360px;
    min-width: 350px;
    max-width: 380px;
    flex-shrink: 0;
  }
}

.settings-search-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
}

.search-icon {
  position: absolute;
  left: 12px;
  color: #94a3b8;
  pointer-events: none;
}

.clear-search-btn {
  position: absolute;
  right: 10px;
  cursor: pointer;
  border: none;
  background: transparent;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.settings-search-input {
  width: 100%;
  height: 42px;
  padding: 0 34px 0 36px;
  font-size: 14px;
  background-color: #ffffff;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  color: #1e293b;
  outline: none;
  transition: all 0.15s ease-in-out;
  box-sizing: border-box;
}

.settings-search-input:focus {
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.settings-search-input::placeholder {
  color: #94a3b8;
}

.category-title {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  color: #94a3b8;
  text-transform: uppercase;
  padding-left: 8px;
  margin-bottom: 6px;
}

.settings-menu-item {
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  padding: 8px 10px;
  border-radius: 14px;
  cursor: pointer;
  border: none;
  outline: none;
  background-color: transparent;
  text-align: left;
  transition: all 0.15s ease-in-out;
  user-select: none;
  box-sizing: border-box;
}

.settings-menu-item:hover:not(.settings-item-active) {
  background-color: #f8fafc;
}

.settings-item-active {
  background-color: #edf5ff !important;
  border-left: 3.5px solid #1d4ed8 !important;
}

.settings-icon-box {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-right: 12px;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.settings-icon-active {
  background-color: #1d4ed8 !important;
}

.settings-icon-inactive {
  background-color: #f1f5f9;
}

.item-title {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.25;
}

.item-title-active {
  font-weight: 700;
  color: #0f172a;
}

.item-description {
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
  line-height: 1.25;
}

.settings-content-card {
  background-color: #ffffff;
  border-radius: 20px;
  border: 1px solid #eef2f6;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
  box-sizing: border-box;
  overflow-y: auto;
  max-height: 85vh;
}

.hide-scrollbar::-webkit-scrollbar {
  display: none;
}
.hide-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

/* Dark mode */
.dark .settings-sidebar-card,
.dark .settings-content-card {
  background-color: #1f1f1f !important;
  border-color: #333333 !important;
}

.dark .settings-search-input {
  background-color: #2a2a2a !important;
  border-color: #404040 !important;
  color: #ffffff !important;
}

.dark .settings-item-active {
  background-color: rgba(37, 99, 235, 0.18) !important;
  border-left-color: #60a5fa !important;
}

.dark .settings-icon-inactive {
  background-color: #2a2a2a !important;
}

.dark .item-title {
  color: #e2e8f0;
}

.dark .item-title-active {
  color: #ffffff;
}

.dark .item-description {
  color: #94a3b8;
}

.dark .settings-menu-item:hover:not(.settings-item-active) {
  background-color: #262626 !important;
}
</style>
